<script lang="ts">
  import { onMount } from 'svelte';
  import { VARIANTS, isVariantId, type MaxQuality } from '@multiview/shared';
  import { api, type RuntimeSettings, type SettingsView } from '../lib/api/client.ts';
  import {
    AUTO_MULTI, audioAnalysisPreferred, getMaxQuality, matchRefreshPreferred,
    setAudioAnalysisPreferred, setMatchRefreshPreferred, setMaxQuality,
  } from '../lib/player/quality.ts';
  import { canMatchRefresh, resetFrameRate } from '../lib/player/display.ts';

  interface Props { onClose: () => void }
  const { onClose }: Props = $props();

  let view = $state<SettingsView | null>(null);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let saved = $state<string | null>(null);

  onMount(() => {
    api.settings()
      .then((v) => { view = v; })
      .catch((err) => { error = err instanceof Error ? err.message : String(err); });
  });

  async function update(patch: Partial<RuntimeSettings>, note: string) {
    busy = true;
    error = null;
    saved = null;
    try {
      view = await api.updateSettings(patch);
      saved = note;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }

  const DEINTERLACE = [
    { value: 'off', label: 'Aus' },
    { value: 'auto', label: 'Automatisch' },
  ] as const;

  const deint = $derived(view?.capabilities.deinterlace ?? null);

  /* -------------------------------------------------- playback on this device */

  let maxQuality = $state<MaxQuality>(getMaxQuality());
  let matchRefresh = $state(matchRefreshPreferred());
  let audioAnalysis = $state(audioAnalysisPreferred());
  /** The graph is set up once per page load; a change needs a reload. */
  const audioAnalysisAtStart = audioAnalysisPreferred();
  const variantsCap = $derived(view?.capabilities.variants ?? null);

  const autoLabel = AUTO_MULTI === 'source'
    ? 'Automatisch (Original)'
    : `Automatisch (Original, bei zwei großen Bildern ${AUTO_MULTI})`;

  const MAX_QUALITY: Array<{ value: MaxQuality; label: string }> = [
    { value: 'auto', label: autoLabel },
    { value: 'source', label: 'Original' },
    ...(['1080p25', '720p50', '720p25', '540p25'] as const).map((id) => ({ value: id, label: VARIANTS[id].label })),
  ];

  function chooseMaxQuality(value: MaxQuality) {
    maxQuality = value;
    setMaxQuality(value);
    saved = isVariantId(value) && variantsCap && !variantsCap.available
      ? 'Gespeichert – der Server kann aber keine reduzierten Stufen erzeugen, es bleibt beim Original.'
      : 'Gespeichert. Gilt sofort für laufende Streams auf diesem Gerät.';
  }

  function chooseMatchRefresh(on: boolean) {
    matchRefresh = on;
    setMatchRefreshPreferred(on);
    if (!on) resetFrameRate();
    saved = 'Gespeichert.';
  }

  function chooseAudioAnalysis(on: boolean) {
    audioAnalysis = on;
    setAudioAnalysisPreferred(on);
    saved = on === audioAnalysisAtStart ? 'Gespeichert.' : 'Gespeichert. Wirkt nach einem Neustart der App bzw. Neuladen der Seite.';
  }

  /* ------------------------------------------------------------ google tv app */

  interface ApkInfo { versionCode: number; versionName: string; builtAt: number }

  let apk = $state<ApkInfo | null>(null);
  /** Version of the wrapper this page runs in, from its user agent; null in a browser. */
  const installedCode = (() => {
    const m = /MultiviewTV\/(\d+)/.exec(navigator.userAgent);
    return m ? Number(m[1]) : null;
  })();
  const apkUrl = `${location.origin}/tv.apk`;
  const updateAvailable = $derived(apk !== null && installedCode !== null && apk.versionCode > installedCode);

  onMount(() => {
    fetch('/downloads/tv-apk.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() as Promise<ApkInfo> : null))
      .then((info) => { apk = info; })
      .catch(() => {});
  });

  const datum = (ms: number) => new Date(ms).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
</script>

<div class="settings scroll">
  <div class="card">
    <header class="head">
      <h1>Einstellungen</h1>
      <button data-focusable data-focus-default type="button" onclick={onClose}>Zurück</button>
    </header>

    {#if !view && !error}
      <p class="faint">Lade …</p>
    {/if}

    <section>
      <h2>Wiedergabe auf diesem Gerät</h2>
      <p class="muted">
        Höchste Qualität für große Bilder. Kleinere Stufen erzeugt der Server auf seiner GPU aus
        derselben Verbindung zum Anbieter – sie kosten keinen zusätzlichen Stream, entlasten aber
        schwächere Fernseher und Beamer spürbar. <b>Automatisch</b> stuft selbst weiter herunter,
        wenn große Bilder anhaltend Einzelbilder verlieren, und bleibt dann bis zum nächsten Spiel
        dabei. Gilt nur für dieses Gerät.
      </p>
      <div class="choices">
        {#each MAX_QUALITY as option (option.value)}
          <button
            data-focusable
            type="button"
            class:active={maxQuality === option.value}
            onclick={() => chooseMaxQuality(option.value)}
          >{option.label}</button>
        {/each}
      </div>
      {#if variantsCap && !variantsCap.available}
        <p class="warn small">Reduzierte Stufen nicht verfügbar: {variantsCap.reason} – es wird immer das Original gespielt.</p>
      {:else if variantsCap?.lastFault}
        <p class="warn small">{variantsCap.lastFault}</p>
      {/if}
      <p class="faint small">
        „Halbe Bildrate“ heißt 25 statt 50 Bilder pro Sekunde. Wechselt die Stufe eines laufenden
        Kanals, startet dessen Stream einmal kurz neu.
      </p>

      {#if canMatchRefresh()}
        <h2>Bildwiederholrate anpassen</h2>
        <p class="muted">
          Stellt den Fernseher auf 50 Hz, wenn der Sender 50 Bilder pro Sekunde liefert (bzw. 60 Hz
          bei 60). Ohne das zeigt eine 60-Hz-Ausgabe jedes fünfte Bild doppelt – gleichmäßiges
          Ruckeln bei jedem Schwenk. Beim Umschalten ist das Bild ein, zwei Sekunden schwarz.
        </p>
        <div class="choices">
          <button data-focusable type="button" class:active={matchRefresh} onclick={() => chooseMatchRefresh(true)}>Ein</button>
          <button data-focusable type="button" class:active={!matchRefresh} onclick={() => chooseMatchRefresh(false)}>Aus</button>
        </div>
      {:else if window.MultiviewTV}
        <p class="faint small">Zum Anpassen der Bildwiederholrate braucht es ein Update der App (unten).</p>
      {/if}

      <h2>Tonanalyse (Jubel-Erkennung)</h2>
      <p class="muted">
        Misst die Lautstärke aller Kacheln, damit „Bei Jubel“ umschalten kann. Am PC hört der
        Browser selbst mit; auf Fernsehern und Handys misst der Server den Pegel, damit das Bild
        nicht ins Stocken gerät.{#if !audioAnalysis} „Bei Jubel“ reagiert so auf diesem Gerät nicht.{/if}
      </p>
      <div class="choices">
        <button data-focusable type="button" class:active={audioAnalysis} onclick={() => chooseAudioAnalysis(true)}>Ein</button>
        <button data-focusable type="button" class:active={!audioAnalysis} onclick={() => chooseAudioAnalysis(false)}>Aus</button>
      </div>
    </section>

    {#if view}
      <section>
        <h2>Zeilensprung entfernen</h2>
        <p class="muted">
          Manche Sender liefern 1080i: zwei Halbbilder statt eines Vollbilds. Im Browser zeigt
          das bei schnellen Bewegungen feine Kammstreifen. <b>Automatisch</b> erkennt solche
          Kanäle und wandelt sie auf der GPU in 1080p mit 50 Vollbildern pro Sekunde um —
          Kanäle, die schon progressiv sind, bleiben unangetastet.
        </p>
        <div class="choices">
          {#each DEINTERLACE as option (option.value)}
            <button
              data-focusable
              type="button"
              class:active={view.settings.deinterlace === option.value}
              disabled={busy || (option.value === 'auto' && !deint?.available)}
              onclick={() => update({ deinterlace: option.value }, 'Gespeichert. Laufende Streams starten neu.')}
            >{option.label}</button>
          {/each}
        </div>
        {#if deint && !deint.available}
          <p class="warn">Nicht verfügbar: {deint.reason}</p>
        {:else if deint?.lastFault}
          <p class="warn small">Ein Kanal blieb unverändert, weil die Umwandlung scheiterte: {deint.lastFault}</p>
        {/if}
        <p class="faint small">
          Kostet etwa 0,2–0,5 s zusätzliche Verzögerung und beim ersten Öffnen eines solchen
          Kanals einmalig ein paar Sekunden länger Laden. Die CPU-Last bleibt praktisch gleich.
          Ändern startet laufende Streams kurz neu.
        </p>
      </section>
    {/if}

    <section>
      <h2>Google-TV-App</h2>
      {#if installedCode !== null}
        <p class="muted">
          Du nutzt die App. Installiert: Build {installedCode}{#if apk} · auf dem Server: Build {apk.versionCode} vom {datum(apk.builtAt)}{/if}.
        </p>
        {#if updateAvailable && window.MultiviewTV}
          <button data-focusable type="button" class="primary" onclick={() => window.MultiviewTV?.installUpdate('/tv.apk')}>
            Update installieren
          </button>
        {:else if apk}
          <p class="faint small">Die App ist aktuell.</p>
        {/if}
      {:else if apk}
        <p class="muted">
          Eine kleine App für Google TV und Android TV: Vollbild, Fernbedienung mit Zurück,
          Kanalwippe und Farbtasten. Updates kommen danach direkt über dieses Menü.
        </p>
        <a class="button primary" href="/tv.apk" download="multiview-tv.apk" data-focusable>APK herunterladen</a>
        <span class="faint small">Build {apk.versionCode} · {datum(apk.builtAt)}</span>
        <ol class="steps small">
          <li>Auf dem Fernseher die App <b>Downloader</b> (von AFTVnews) aus dem Play Store installieren.</li>
          <li>Einstellungen → System → Info → 7× auf <b>Android TV-Betriebssystem-Build</b> drücken (Entwickleroptionen),
            dann unter Apps → Sicherheit → <b>Unbekannte Apps</b> dem Downloader die Installation erlauben.</li>
          <li>Im Downloader diese Adresse eingeben: <code>{apkUrl}</code></li>
          <li>Installieren, öffnen, anmelden — fertig.</li>
        </ol>
      {:else}
        <p class="faint">Auf diesem Server liegt keine APK.</p>
      {/if}
    </section>

    {#if error}<p class="error">{error}</p>{/if}
    {#if saved}<p class="success">{saved}</p>{/if}
  </div>
</div>

<style>
  .settings {
    height: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: var(--safe-inset);
  }

  .card {
    width: min(46em, 100%);
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: calc(var(--pad) * 1.5);
  }

  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--pad); }
  h1 { margin: 0 0 0.4em; font-size: var(--font-xl); }
  h2 { margin: var(--pad) 0 0.4em; font-size: var(--font-lg); }
  p { line-height: 1.5; }
  p.muted { margin: 0 0 var(--pad); }
  .small { font-size: var(--font-sm); }

  .choices { display: flex; gap: var(--gap); flex-wrap: wrap; }
  .choices button.active { border-color: var(--accent); color: var(--accent); }
  .choices button:disabled { opacity: 0.45; cursor: default; }

  .warn { color: var(--warn); margin: var(--gap) 0 0; }

  .primary { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }
  a.button {
    display: inline-block;
    margin-right: var(--gap);
    padding: 0.55em 0.9em;
    border: 1px solid var(--accent);
    border-radius: var(--radius);
    color: inherit;
    text-decoration: none;
  }
  .steps { margin: var(--pad) 0 0; padding-left: 1.4em; line-height: 1.6; color: var(--text-dim); }
  code { color: var(--text); user-select: all; }
  .error { margin: var(--pad) 0 0; color: var(--danger); }
  .success { margin: var(--pad) 0 0; color: var(--accent); }
</style>
